import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePath } from 'react-router';
import pages from 'pages';
import Header from 'shared/components/Header/Header';
import { Card } from 'shared/components/Card/Card';
import { InfoDrawer } from 'shared/components/InfoDrawer/InfoDrawer';
import { AgreementCard } from 'modules/support/components/AgreementCard/AgreementCard';
import { SupportContactCard } from 'modules/support/components/SupportContactCard/SupportContactCard';
import {
  RequiredTasks,
  TransferRequiredTaskState,
} from 'modules/support/components/RequiredTasks/RequiredTasks';
import { TransferInfoDrawerContent } from 'modules/support/components/TransferInfoDrawerContent/TransferInfoDrawerContent';
import { useUser } from 'hooks/useUser';
import { useGetHomeTransferStatus } from 'api/transfer';
import { DispositionCode } from 'api/transfer/types';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { ErrorFallback } from 'shared/components/ErrorBoundary/ErrorFallback';
import { BillingIcon } from 'shared/components/icons/BillingIcon';
import { URLS } from 'constants/urls';
import {
  AgreementTransferStatusWrapper,
  ContentWrapper,
  MainContent,
  Sidebar,
  LoadingContainer,
  TransferCardContent,
  LearnMoreLink,
  SidebarCardBody,
} from './AgreementTransferStatus.styles';
import TransferProgress from '../../components/TransferProgress/TransferProgress';
import { AngleRightToBracketHeavy } from '@sighten/beacon/icons';

export function AgreementTransferStatus() {
  const { selectedAgreement, userResult } = useUser();
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'support&FAQs.faqCategories.agreementTransferStatus',
  });

  const {
    data: transferStatus,
    isLoading: isStatusLoading,
    isError: isStatusError,
  } = useGetHomeTransferStatus(selectedAgreement?.homeowner_contract_id);

  const requiredTask: TransferRequiredTaskState = useMemo(() => {
    if (!transferStatus || transferStatus.billing_onboarding_date) {
      return TransferRequiredTaskState.NONE_REQUIRED;
    }
    switch (transferStatus.disposition_code) {
      case DispositionCode.PREPAY:
        if (transferStatus.transfer_reviewed && !transferStatus.contract_paid_in_full) {
          return TransferRequiredTaskState.PREPAYMENT;
        }
        return TransferRequiredTaskState.NONE_REQUIRED;
      case DispositionCode.PAYOFF:
        if (!transferStatus.contract_paid_in_full) {
          return TransferRequiredTaskState.PAYOFF_PAYMENT;
        }
        return TransferRequiredTaskState.NONE_REQUIRED;
      default:
        if (transferStatus.ucc_filing_fee_date) {
          return TransferRequiredTaskState.UCC_FEE_PAID;
        } else {
          return TransferRequiredTaskState.UCC_FEE_DUE;
        }
    }
  }, [transferStatus]);

  const showLoading = isStatusLoading || userResult?.loading;

  if (!showLoading && isStatusError) {
    return <ErrorFallback />;
  }

  return (
    <AgreementTransferStatusWrapper>
      <Header
        pageName="AGREEMENT_TRANSFER_STATUS"
        title={t('pageTitle')}
        routeBreadCrumbs={[
          {
            label: t('support&FAQs.support&FAQs', { keyPrefix: '' }),
            link: resolvePath(pages.SUPPORT),
          },
          { label: t('pageTitle') },
        ]}
      />

      <ContentWrapper>
        <MainContent>
          {showLoading || !transferStatus ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : (
            <Card title="" width="100%">
              <TransferCardContent>
                <RequiredTasks task={requiredTask} />
                <TransferProgress status={transferStatus} />
                <LearnMoreLink onClick={() => setInfoDrawerOpen(true)}>
                  {t('requiredTasks.learnMore')}
                  <AngleRightToBracketHeavy />
                </LearnMoreLink>
              </TransferCardContent>
            </Card>
          )}
        </MainContent>

        <Sidebar>
          <Card
            title={t('sidebar.billing')}
            icon={{ element: <BillingIcon />, color: 'grey' }}
            url={URLS.EVERBRIGHT_ONLINE_ACCOUNT}
            width="100%"
          >
            <SidebarCardBody>{t('sidebar.billingBody')}</SidebarCardBody>
          </Card>
          {selectedAgreement && (
            <AgreementCard
              agreementSigned={selectedAgreement.contract_signed_date}
              agreementTerm={selectedAgreement.contract_term as number}
              agreementId={selectedAgreement.id}
              showNotice
              hideDetails
            />
          )}
          <SupportContactCard />
        </Sidebar>
      </ContentWrapper>

      <InfoDrawer
        title={t('infoDrawer.title')}
        body={<TransferInfoDrawerContent />}
        isOpen={infoDrawerOpen}
        onCloseInfoDrawer={() => setInfoDrawerOpen(false)}
      />
    </AgreementTransferStatusWrapper>
  );
}

export default AgreementTransferStatus;
